import { useNuxtApp } from "#app";
import { ref, watch, isRef, onUnmounted, shallowRef, toValue, type Ref } from "vue";
import type { ConvexClient } from "convex/browser";
import type { FunctionReference, FunctionArgs, FunctionReturnType } from "convex/server";
import { createLogger } from '#bubba/shared/utils/logger';

const log = createLogger('useConvex');

/**
 * Check if running on server side
 */
function isSSR(): boolean {
  return import.meta.server ?? false;
}

/**
 * Get the Convex client instance.
 *
 * Throws during SSR — the Convex plugin is client-only (`convex.client.ts`),
 * so `$convex` is never available on the server. Composables that are called
 * at setup time (top-level `<script setup>`) must use the lazy variants
 * (`useConvexMutation`, `useConvexAction`) which defer client resolution
 * to invocation time.
 */
export function useConvex(): ConvexClient {
  if (isSSR()) {
    const caller = new Error().stack?.split('\n')[2]?.trim() ?? 'unknown';
    log.error(
      `useConvex() called during SSR — the Convex client is only available client-side. ` +
      `If this runs at component setup time, wrap the call in onMounted() or use ` +
      `useConvexMutation/useConvexAction which resolve the client lazily.\n` +
      `  Called from: ${caller}`
    );
    throw new Error(
      `Convex client is not available during SSR. ` +
      `The convex plugin is client-only (convex.client.ts). ` +
      `Caller: ${caller}`
    );
  }

  const nuxtApp = useNuxtApp();
  const convex = nuxtApp.$convex as ConvexClient | null | undefined;

  if (!convex) {
    const url = nuxtApp.$config?.public?.convexUrl;
    log.error(
      `useConvex() — $convex is ${convex === null ? 'null (plugin ran but URL was missing)' : 'undefined (plugin did not run)'}. ` +
      `NUXT_PUBLIC_CONVEX_URL = ${url ? `"${url}"` : '(not set)'}. ` +
      `Available plugins: ${Object.keys(nuxtApp).filter(k => k.startsWith('$')).join(', ')}`
    );
    throw new Error(
      `Convex client not initialized. $convex is ${convex === null ? 'null' : 'undefined'}. ` +
      `NUXT_PUBLIC_CONVEX_URL = ${url ? '"set"' : '"not set"'}. ` +
      `Check that convex.client.ts plugin loaded successfully.`
    );
  }

  return convex;
}

/**
 * Subscribe to a Convex query with reactive updates.
 * Skips subscription during SSR and subscribes on client hydration.
 *
 * `args` can be:
 *  - a plain object: `{ productId: '...' }` — subscribes once
 *  - a Ref or ComputedRef: re-subscribes when its value changes
 *  - a getter function: `() => condition ? args : 'skip'` — re-subscribes when dependencies change
 *
 * Pass `'skip'` (or a reactive source that returns `'skip'`) to suppress the subscription.
 */
export function useConvexQuery<Query extends FunctionReference<"query">>(
  query: Query,
  args: FunctionArgs<Query> | Ref<FunctionArgs<Query> | 'skip'> | (() => FunctionArgs<Query> | 'skip')
): { data: Ref<FunctionReturnType<Query> | undefined>; error: Ref<Error | null>; isLoading: Ref<boolean> } {
  // BOLT: Use shallowRef for data since Convex results are immutable and replaced wholesale.
  const data = shallowRef<FunctionReturnType<Query> | undefined>(undefined) as Ref<FunctionReturnType<Query> | undefined>;
  const error = ref<Error | null>(null);
  const isLoading = ref(true);

  // During SSR, return empty reactive state — subscription starts on client
  if (isSSR()) {
    log.debug('useConvexQuery() called during SSR — deferring subscription to client hydration');
    return { data, error, isLoading };
  }

  const convex = useConvex();

  let currentUnsubscribe: (() => void) | null = null;

  const subscribe = (currentArgs: FunctionArgs<Query> | 'skip') => {
    // Clean up previous subscription
    if (currentUnsubscribe) {
      currentUnsubscribe();
      currentUnsubscribe = null;
    }

    if (currentArgs === 'skip') {
      data.value = undefined;
      error.value = null;
      isLoading.value = false;
      return;
    }

    // Clear stale state and show loading
    data.value = undefined;
    error.value = null;
    isLoading.value = true;

    currentUnsubscribe = convex.onUpdate(
      query,
      currentArgs,
      (result: FunctionReturnType<Query>) => {
        data.value = result;
        isLoading.value = false;
        error.value = null;
      },
      (err: Error) => {
        log.error('[useConvexQuery] Subscription error:', err);
        error.value = err;
        isLoading.value = false;
      }
    );
  };

  // Initial subscription
  subscribe(toValue(args));

  // Watch for arg changes (JSON.stringify correctly detects deep changes and avoids redundant resubscriptions)
  if (isRef(args) || typeof args === 'function') {
    watch(
      () => JSON.stringify(toValue(args)),
      () => subscribe(toValue(args))
    );
  }

  onUnmounted(() => {
    if (currentUnsubscribe) currentUnsubscribe();
  });

  return { data, error, isLoading };
}

/**
 * Get a function to call a Convex mutation.
 * Safe to call at setup time during SSR — the client is resolved lazily
 * when the returned function is actually invoked (always client-side).
 */
export function useConvexMutation<Mutation extends FunctionReference<"mutation">>(
  mutation: Mutation
): (args: FunctionArgs<Mutation>) => Promise<FunctionReturnType<Mutation>> {
  // Capture nuxtApp ref at setup time (works in both SSR and client)
  const nuxtApp = useNuxtApp();

  return (args: FunctionArgs<Mutation>) => {
    const convex = nuxtApp.$convex as ConvexClient | null | undefined;
    if (!convex) {
      const errMsg =
        `Convex client not available when calling mutation. ` +
        `$convex is ${convex === null ? 'null' : 'undefined'}. ` +
        `This should only be called client-side after the convex plugin has initialized.`;
      log.error(errMsg);
      return Promise.reject(new Error(errMsg));
    }
    return convex.mutation(mutation, args);
  };
}

/**
 * Get a function to call a Convex action.
 * Safe to call at setup time during SSR — the client is resolved lazily
 * when the returned function is actually invoked (always client-side).
 */
export function useConvexAction<Action extends FunctionReference<"action">>(
  action: Action
): (args: FunctionArgs<Action>) => Promise<FunctionReturnType<Action>> {
  // Capture nuxtApp ref at setup time (works in both SSR and client)
  const nuxtApp = useNuxtApp();

  return (args: FunctionArgs<Action>) => {
    const convex = nuxtApp.$convex as ConvexClient | null | undefined;
    if (!convex) {
      const errMsg =
        `Convex client not available when calling action. ` +
        `$convex is ${convex === null ? 'null' : 'undefined'}. ` +
        `This should only be called client-side after the convex plugin has initialized.`;
      log.error(errMsg);
      return Promise.reject(new Error(errMsg));
    }
    return convex.action(action, args);
  };
}
